import React, { useState } from 'react'
import CreatableSelect from 'react-select/creatable'
import { ReactSelectLooopStyles, SelectVariant } from '../select/Select'
import './TagSelect.less'
import {
  useAddTagToEventMutation,
  useAddTagToPathwayMutation,
  useAddTagToRecurringEventMutation,
  useAddTagToSurveyMutation,
  useAddTagToWorkspaceMutation,
  useCreateTagMutation,
  useGetTagsQuery,
  useRemoveAllTagsFromEventMutation,
  useRemoveAllTagsFromPathwayMutation,
  useRemoveAllTagsFromRecurringEventMutation,
  useRemoveAllTagsFromSurveyMutation,
  useRemoveAllTagsFromWorkspaceMutation,
  useRemoveTagFromEventMutation,
  useRemoveTagFromPathwayMutation,
  useRemoveTagFromRecurringEventMutation,
  useRemoveTagFromSurveyMutation,
  useRemoveTagFromWorkspaceMutation
} from 'front-end/src/components/common/tag/TagSelect.codegen'
import { Spinner } from 'front-end/src/components'

type TagType = {
  id: number
  name: string
}

export enum TAG_TARGET_TYPES {
  event = 'event',
  pathway = 'survey',
  survey = 'pathway',
  recurringEvent = 'recurringEvent',
  workspace = 'workspace'
}

export type TagTargetEntityType = {
  type: TAG_TARGET_TYPES
  id: number
}

type TagSelectProps = {
  target?: TagTargetEntityType
  defaultValue: TagType[]
  onChange?: (value: TagType[]) => void
  autoSave?: boolean
  autoCreate?: boolean
  placeholder?: string
}

type TagSelectState = {
  tagOptions: any
  selected: any
}

export const TagSelect = ({
  target,
  defaultValue,
  onChange,
  autoCreate = true,
  autoSave = true,
  placeholder = ''
}: TagSelectProps) => {
  const [addTagToEvent] = useAddTagToEventMutation()
  const [addTagToPathway] = useAddTagToPathwayMutation()
  const [addTagToSurvey] = useAddTagToSurveyMutation()
  const [addTagToRecurringEvent] = useAddTagToRecurringEventMutation()
  const [addTagToWorkspace] = useAddTagToWorkspaceMutation()

  const [removeTagFromEvent] = useRemoveTagFromEventMutation()
  const [removeTagFromPathway] = useRemoveTagFromPathwayMutation()
  const [removeTagFromSurvey] = useRemoveTagFromSurveyMutation()
  const [removeTagFromRecurringEvent] = useRemoveTagFromRecurringEventMutation()
  const [removeTagFromWorkspace] = useRemoveTagFromWorkspaceMutation()

  const [removeAllTagsFromEvent] = useRemoveAllTagsFromEventMutation()
  const [removeAllTagsFromPathway] = useRemoveAllTagsFromPathwayMutation()
  const [removeAllTagsFromSurvey] = useRemoveAllTagsFromSurveyMutation()
  const [removeAllTagsFromRecurringEvent] =
    useRemoveAllTagsFromRecurringEventMutation()
  const [removeAllTagsFromWorkspace] = useRemoveAllTagsFromWorkspaceMutation()

  const [state, setState] = useState<TagSelectState>({
    tagOptions: [],
    selected: defaultValue || []
  })

  const changeState = (changes: Partial<TagSelectState>) => {
    setState({
      ...state,
      ...changes
    })
  }

  const { loading } = useGetTagsQuery({
    onCompleted: (data) => {
      changeState({
        tagOptions: data.tags
      })
    }
  })

  const [createTag] = useCreateTagMutation({
    onCompleted: async (data) => {
      const { createTag } = data.admin
      if (autoSave) {
        await addTag(createTag.id)
      }
      changeState({
        tagOptions: [createTag, ...state.tagOptions],
        selected: [...state.selected, createTag]
      })

      const newValue = [...state.selected, createTag]
      onChange && newValue && onChange(newValue)
    }
  })

  if (loading) {
    return <Spinner />
  }

  const addTag = (tagId: number | string) => {
    const variables = {
      entityId: target?.id,
      tagId
    }

    switch (target?.type) {
      case TAG_TARGET_TYPES.event:
        return addTagToEvent({ variables })
      case TAG_TARGET_TYPES.pathway:
        return addTagToPathway({ variables })
      case TAG_TARGET_TYPES.survey:
        return addTagToSurvey({ variables })
      case TAG_TARGET_TYPES.recurringEvent:
        return addTagToRecurringEvent({ variables })
      case TAG_TARGET_TYPES.workspace:
        return addTagToWorkspace({ variables })
    }
  }

  const removeTag = async (tagId: number | string) => {
    const variables = {
      entityId: target?.id,
      tagId: tagId
    }

    switch (target?.type) {
      case TAG_TARGET_TYPES.event:
        await removeTagFromEvent({ variables })
        break
      case TAG_TARGET_TYPES.pathway:
        removeTagFromPathway({ variables })
        break
      case TAG_TARGET_TYPES.survey:
        removeTagFromSurvey({ variables })
        break
      case TAG_TARGET_TYPES.recurringEvent:
        removeTagFromRecurringEvent({ variables })
        break
      case TAG_TARGET_TYPES.workspace:
        removeTagFromWorkspace({ variables })
        break
    }
  }

  const removeAllTags = async () => {
    const variables = {
      entityId: target?.id
    }

    switch (target?.type) {
      case TAG_TARGET_TYPES.event:
        removeAllTagsFromEvent({ variables })
        break
      case TAG_TARGET_TYPES.pathway:
        removeAllTagsFromPathway({ variables })
        break
      case TAG_TARGET_TYPES.survey:
        removeAllTagsFromSurvey({ variables })
        break
      case TAG_TARGET_TYPES.recurringEvent:
        removeAllTagsFromRecurringEvent({ variables })
        break
      case TAG_TARGET_TYPES.workspace:
        removeAllTagsFromWorkspace({ variables })
        break
    }
  }

  const handleChange = async (val, actionMeta) => {
    if (autoSave) {
      if (actionMeta.option) {
        await addTag(actionMeta.option.value)
      } else if (actionMeta.removedValue) {
        await removeTag(actionMeta.removedValue.value)
      } else if (actionMeta.action === 'clear') {
        await removeAllTags()
      }
    }

    const newValue = formatForTags(val)
    changeState({ selected: newValue })
    onChange && newValue && onChange(newValue)
  }

  const handleCreate = async (val) => {
    if (autoCreate) {
      await createTag({
        variables: {
          name: val
        }
      })
    } else {
      const newTag = { name: val }
      changeState({
        tagOptions: [newTag, ...state.tagOptions],
        selected: [...state.selected, newTag]
      })
      const newValue = [...state.selected, newTag]
      onChange && newValue && onChange(newValue)
    }
  }

  const formatForTags = (values) =>
    (values || []).map(({ label, value }) => ({ id: value, name: label }))

  const formatForSelect = (tags: TagType[] = []) =>
    tags?.map(({ id, name }) => {
      return { label: name, value: id }
    }) || []

  const { tagOptions, selected } = state

  return (
    <CreatableSelect
      className="TagSelect"
      classNamePrefix="TagSelect"
      isClearable={false}
      isMulti
      placeholder={placeholder}
      onChange={handleChange}
      onCreateOption={handleCreate}
      options={formatForSelect(tagOptions)}
      value={formatForSelect(selected)}
      styles={ReactSelectLooopStyles(SelectVariant.GREY)}
    />
  )
}

export default TagSelect
